<template>
  <b-modal
    :id="MODAL_ID"
    size="lg"
    centered
    dialog-class="resize-template-modal"
    body-class="resize-template-modal__body"
    hide-header
    hide-backdrop
    @ok.prevent="onSave"
    @shown="onShown"
  >
    <div class="resize-template-modal__header">
      <div>
        <h5>{{ title }}</h5>
        <div
          v-if="selectedTemplateSize"
          class="resize-template-modal__original-size"
        >
          Original {{ formattedSelectedTemplateSize }}
        </div>
      </div>
      <b-button-close
        aria-label="Close"
        @click="onClose"
      />
    </div>

    <div class="resize-template-modal__content">
      <section
        v-if="selectedSizes.length"
        class="resize-template-modal__selected-sizes"
      >
        <div class="resize-template-modal__section-heading">
          <span>{{ isMultiSizes ? 'New sizes' : 'New size' }}</span>
          <button
            type="button"
            @click="clearSelectedSizes"
          >
            Clear all
          </button>
        </div>
        <div class="resize-template-modal__chips">
          <button
            v-for="(size, index) in selectedSizes"
            :key="size.template ? `template-${size.template.id}` : `${size.width}-${size.height}-${index}`"
            type="button"
            class="resize-template-modal__chip"
            :title="size.template ? `Mapped to ${size.template.name}` : ''"
            @click="onRemoveSize(index)"
          >
            {{ size.width }} × {{ size.height }}
            <b-icon icon="x" />
          </button>
        </div>
      </section>

      <div
        class="resize-template-modal__tabs"
        role="tablist"
      >
        <button
          v-for="tab in RESIZE_TABS"
          :key="tab.id"
          type="button"
          :class="{ active: activeTab === tab.id }"
          @click="selectTab(tab.id)"
        >
          <b-icon :icon="tab.icon" />
          {{ tab.label }}
        </button>
      </div>

      <section class="resize-template-modal__picker">
        <template v-if="activeTab === TAB_TYPES.PRESETS">
          <div class="resize-template-modal__search">
            <b-icon icon="search" />
            <input
              v-model.trim="presetSearch"
              type="search"
              placeholder="Search size or name..."
            >
          </div>
          <div class="resize-template-modal__catalog">
            <div
              v-for="group in filteredPresetGroups"
              :key="group.name"
              class="resize-template-modal__preset-group"
            >
              <div class="resize-template-modal__group-title">
                {{ group.name }}
              </div>
              <div class="resize-template-modal__card-grid">
                <button
                  v-for="size in group.sizes"
                  :key="size.id"
                  type="button"
                  class="resize-template-modal__size-card"
                  :class="{ selected: isSizeSelected(size) }"
                  @click="togglePresetSize(size)"
                >
                  <span
                    class="resize-template-modal__size-preview"
                    :style="getSizePreviewStyle(size)"
                  />
                  <span class="resize-template-modal__card-copy">
                    <strong>{{ size.width }} × {{ size.height }}</strong>
                    <small>{{ size.name }}</small>
                  </span>
                  <b-icon
                    v-if="isSizeSelected(size)"
                    icon="check"
                  />
                </button>
              </div>
            </div>
            <div
              v-if="!filteredPresetGroups.length"
              class="resize-template-modal__empty"
            >
              No sizes found
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === TAB_TYPES.TEMPLATES">
          <div class="resize-template-modal__template-toolbar">
            <div class="resize-template-modal__search">
              <b-icon icon="search" />
              <input
                v-model.trim="templateSearch"
                type="search"
                placeholder="Search templates..."
              >
            </div>
            <Multiselect
              v-model="selectedCustomer"
              class="resize-template-modal__customer"
              track-by="name"
              label="name"
              placeholder="Select customer"
              :options="customerOptions"
              :show-labels="false"
              @input="onChangeCustomer"
            />
          </div>
          <div class="resize-template-modal__template-filters">
            <div>
              <button
                v-for="filter in TEMPLATE_ORIENTATION_FILTERS"
                :key="filter.value"
                type="button"
                :class="{ active: selectedTypeTemplate === filter.value }"
                @click="selectedTypeTemplate = filter.value"
              >
                {{ filter.label }}
              </button>
            </div>
            <b-form-checkbox
              v-model="galleryView"
              switch
            >
              Gallery view
            </b-form-checkbox>
          </div>
          <div
            class="resize-template-modal__template-list"
            :class="{ 'gallery-view': galleryView }"
          >
            <button
              v-for="template in filteredTemplates"
              :key="template.id || template.name"
              type="button"
              class="resize-template-modal__template-card"
              :class="{ selected: isTemplateSelected(template) }"
              @click="toggleTemplate(template)"
            >
              <span class="resize-template-modal__template-size-preview">
                <span :style="getSizePreviewStyle(template)" />
              </span>
              <span class="resize-template-modal__card-copy">
                <strong>{{ template.name }}</strong>
                <small>{{ template.width }} × {{ template.height }}<template v-if="getTemplateCustomerName(template)"> · {{ getTemplateCustomerName(template) }}</template></small>
              </span>
              <b-icon
                v-if="isTemplateSelected(template)"
                icon="check"
              />
            </button>
            <div
              v-if="isLoading"
              class="resize-template-modal__empty"
            >
              Loading templates...
            </div>
            <div
              v-else-if="!selectedCustomer"
              class="resize-template-modal__empty"
            >
              Select a customer to browse templates
            </div>
            <div
              v-else-if="!filteredTemplates.length"
              class="resize-template-modal__empty"
            >
              No templates found
            </div>
          </div>
        </template>

        <template v-else>
          <div class="resize-template-modal__custom-size">
            <div class="resize-template-modal__custom-preview">
              <span :style="customSizePreviewStyle" />
            </div>
            <div class="resize-template-modal__custom-controls">
              <div class="resize-template-modal__dimensions">
                <label>
                  Width
                  <b-form-input
                    ref="customSizeWidth"
                    v-model.number="customSize.width"
                    min="1"
                    type="number"
                    placeholder="Width"
                    @input="(value) => onCustomSizeChange(value, CUSTOM_SIZE_INPUT_TYPES.WIDTH_INPUT)"
                  />
                </label>
                <span>×</span>
                <label>
                  Height
                  <b-form-input
                    v-model.number="customSize.height"
                    min="1"
                    type="number"
                    placeholder="Height"
                    @input="(value) => onCustomSizeChange(value, CUSTOM_SIZE_INPUT_TYPES.HEIGHT_INPUT)"
                    @keyup.enter.prevent="onAddCustomSize()"
                  />
                </label>
              </div>
              <b-form-checkbox
                v-model="maintainAspectRatio"
                switch
                @change="onMaintainAspectRatioChange"
              >
                Lock aspect ratio
              </b-form-checkbox>
            </div>
            <b-button
              variant="primary"
              block
              :disabled="!isCustomSizeValid"
              @click="onAddCustomSize()"
            >
              <b-icon icon="plus" /> {{ customSizeButtonLabel }}
            </b-button>
          </div>
        </template>
      </section>

      <button
        type="button"
        class="resize-template-modal__advanced-toggle"
        :aria-expanded="advancedOptionsVisible ? 'true' : 'false'"
        @click="advancedOptionsVisible = !advancedOptionsVisible"
      >
        <span><b-icon icon="sliders" /> Advanced options</span>
        <b-icon :icon="advancedOptionsVisible ? 'chevron-up' : 'chevron-down'" />
      </button>

      <section
        v-show="advancedOptionsVisible"
        class="resize-template-modal__advanced"
      >
        <label>Edit background (optional)</label>
        <Multiselect
          v-model="selectedBackground"
          class="background-dropdown"
          track-by="id"
          label="name"
          placeholder="Choose background"
          :options="getBackgroundFields"
        >
          <template #singleLabel="props">
            <div class="background-dropdown-label">
              <div class="background-dropdown-label__image">
                <img
                  :src="props.option.thumbnail"
                  :alt="props.option.name"
                >
              </div>
              <p class="background-dropdown-label__text">
                {{ props.option.name }}
              </p>
            </div>
          </template>
          <template #option="props">
            <div class="background-dropdown-option">
              <div class="background-dropdown-option__image">
                <img
                  :src="props.option.thumbnail"
                  :alt="props.option.name"
                >
              </div>
              <p class="background-dropdown-option__text">
                {{ props.option.name }}
              </p>
            </div>
          </template>
        </Multiselect>
        <div class="resize-template-modal__option-row">
          <span>Create a base template on the system with the new size</span>
          <b-form-checkbox
            v-model="createBasetemplate"
            switch
            class="m-0"
          />
        </div>
        <div class="resize-template-modal__option-row">
          <span>Auto-position objects</span>
          <b-form-checkbox
            v-model="isAutoPosition"
            switch
            class="m-0"
          />
        </div>
      </section>
    </div>

    <template #modal-footer="{ cancel, ok }">
      <div class="resize-template-modal__footer">
        <b-button
          variant="link"
          @click="cancel"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedSizes.length || isSaving"
          @click="ok"
        >
          <span
            v-if="isSaving"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          <b-icon
            v-else
            icon="stars"
          />
          {{ submitButtonLabel }}
        </b-button>
      </div>
      <div
        v-if="progress.total > 0"
        class="mt-4 w-100"
      >
        <ProgressBarComponent
          :total="progress.total"
          :current="progress.current"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { copyAndResizeEventBroker, EVENTS } from '@frontend/group/modules/copy-and-resize/event-broker';
import TEMPLATE_API from '@frontend/services/api/template';
import { BACKGROUND_PROPERTIES } from '@frontend/group/modules/copy-and-resize/handlers/resize-background-handler';
import { FIELD_TYPE_BACKGROND_THEME_IMAGE } from '@frontend/constants/type-fields-of-template';
import { getTypeDimension } from '@frontend/group/modules/copy-and-resize/helpers';
import { TYPE_DIMENSION } from '@frontend/group/modules/copy-and-resize/handlers/resize-template-handler';
import { ALIGN_CONTENT } from '@frontend/group/modules/copy-and-resize/helpers';
import { FIELD_NAME_IMAGE_AREA } from '@frontend/constants/name-fields-of-template';
import ProgressBarComponent from "@frontend/components/ProgressBarComponent.vue";

const MODAL_ID = 'copy-and-resize-template-modal';

const ALIGN_CONTENT_HORIZONTAL = {
  [ALIGN_CONTENT.START]: 'Left',
  [ALIGN_CONTENT.CENTER]: 'Middle',
  [ALIGN_CONTENT.END]: 'Right'
}

const ALIGN_CONTENT_VERTICAL = {
  [ALIGN_CONTENT.START]: 'Top',
  [ALIGN_CONTENT.CENTER]: 'Middle',
  [ALIGN_CONTENT.END]: 'Bottom'
}

const CUSTOM_SIZE_INPUT_TYPES = {
  WIDTH_INPUT: 'width',
  HEIGHT_INPUT: 'height',
}

const TAB_TYPES = {
  PRESETS: 'presets',
  TEMPLATES: 'templates',
  CUSTOM: 'custom',
};

const RESIZE_TABS = [
  { id: TAB_TYPES.PRESETS, label: 'Preset sizes', icon: 'diagram3' },
  { id: TAB_TYPES.TEMPLATES, label: 'From template', icon: 'grid' },
  { id: TAB_TYPES.CUSTOM, label: 'Custom size', icon: 'tools' },
];

const PRESET_SIZE_GROUPS = [
  {
    name: 'Display',
    sizes: [
      { id: 'medium-rectangle', width: 300, height: 250, name: 'Medium Rectangle' },
      { id: 'leaderboard', width: 728, height: 90, name: 'Leaderboard' },
      { id: 'wide-skyscraper', width: 160, height: 600, name: 'Wide Skyscraper' },
      { id: 'rectangle', width: 180, height: 150, name: 'Rectangle' },
      { id: 'large-rectangle', width: 336, height: 280, name: 'Large Rectangle' },
      { id: 'mobile-banner', width: 320, height: 50, name: 'Mobile Banner' },
    ],
  },
  {
    name: 'Social',
    sizes: [
      { id: 'square-post', width: 1080, height: 1080, name: 'Square Post' },
      { id: 'portrait-post', width: 1080, height: 1350, name: 'Portrait Post' },
      { id: 'story-reel', width: 1080, height: 1920, name: 'Story / Reel' },
      { id: 'landscape-post', width: 1200, height: 628, name: 'Landscape Post' },
    ],
  },
  {
    name: 'PDP',
    sizes: [
      { id: 'pdp-main', width: 2000, height: 2000, name: 'PDP Main' },
      { id: 'pdp-secondary', width: 1500, height: 1500, name: 'PDP Secondary' },
      { id: 'pdp-hero', width: 2500, height: 2500, name: 'PDP Hero' },
      { id: 'pdp-zoom', width: 3000, height: 3000, name: 'PDP Zoom' },
      { id: 'pdp-max-zoom', width: 4000, height: 4000, name: 'PDP Max Zoom' },
    ],
  },
];

const TEMPLATE_ORIENTATION_FILTERS = [
  { value: 0, label: 'All' },
  { value: 1, label: 'Horizontal' },
  { value: 2, label: 'Vertical' },
];

const ALL_CUSTOMERS_OPTION = { id: 0, name: 'All customers' };

export default {
  components: { ProgressBarComponent, Multiselect },
  props: {
    customers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ALIGN_CONTENT: Object.values(ALIGN_CONTENT),
      ALIGN_CONTENT_HORIZONTAL,
      ALIGN_CONTENT_VERTICAL,
      MODAL_ID,
      CUSTOM_SIZE_INPUT_TYPES,
      BACKGROUND_PROPERTIES,
      TAB_TYPES,
      RESIZE_TABS,
      PRESET_SIZE_GROUPS,
      TEMPLATE_ORIENTATION_FILTERS,
      title: 'Copy and Resize Template',
      isMultiSizes: true,
      isLoading: false,
      isSaving: false,
      preview: null,
      isProportionalDimension: false,
      isUseDestinationTemplateFields: false,
      isAutoPosition: true,
      maintainAspectRatio: true,
      selectedTypeTemplate: 0,
      selectedCustomer: null,
      selectedTemplates: [],
      selectedField: null,
      templatesFilteredByTags: {},
      templates: {},
      selectedSizes: [],
      customSize: {
        width: '',
        height: ''
      },
      backgroundProperties: {
        selectedField: null,
        size: BACKGROUND_PROPERTIES.SIZE.COVER,
        position: BACKGROUND_PROPERTIES.POSITION.LEFT_AND_TOP,
      },
      canvasSize: {
        width: 0,
        height: 0,
      },
      alignContent: {
        originX: ALIGN_CONTENT.START,
        originY: ALIGN_CONTENT.START,
      },
      fields: [],
      objects: [],
      inputGroup: '',
      tags: [],
      galleryView: false,
      activeTab: TAB_TYPES.PRESETS,
      presetSearch: '',
      templateSearch: '',
      advancedOptionsVisible: false,
      createBasetemplate: false,
      progress: {
        total: 0,
        current: 0,
      }
    }
  },
  computed: {
    ...mapGetters('layout',
      ['selectedInstance']
    ),
    selectedTemplateSize() {
      const instance = this.preview ?? this.selectedInstance;
      if (this.preview?.dimension?.length) {
        const { 0: width, 1: height } = this.preview.dimension;
        return `${width}x${height}`;
      } else if (instance?.template) {
        const { width, height } = instance.template;
        return `${width}x${height}`;
      } else if (instance?.canvas) {
        const { width, height } = instance.canvas;
        return `${width}x${height}`;
      } else {
        return '';
      }
    },
    formattedSelectedTemplateSize() {
      return this.selectedTemplateSize.replace('x', ' × ');
    },
    customerOptions() {
      return [
        ALL_CUSTOMERS_OPTION,
        ...this.customers.filter(customer => Number(customer.id) !== ALL_CUSTOMERS_OPTION.id),
      ];
    },
    filteredPresetGroups() {
      const query = this.presetSearch.toLowerCase();
      if (!query) return this.PRESET_SIZE_GROUPS;

      return this.PRESET_SIZE_GROUPS
        .map(group => ({
          ...group,
          sizes: group.sizes.filter(size =>
            `${size.width}x${size.height} ${size.width} × ${size.height} ${size.name}`
              .toLowerCase()
              .includes(query)
          ),
        }))
        .filter(group => group.sizes.length);
    },
    filteredTemplates() {
      const query = this.templateSearch.toLowerCase();
      const templates = this.getTemplates();
      if (!query) return templates;

      return templates.filter(template =>
        `${template.name} ${template.width}x${template.height}`.toLowerCase().includes(query)
      );
    },
    customSizePreviewStyle() {
      const width = Math.max(Number(this.customSize.width) || this.canvasSize.width || 1, 1);
      const height = Math.max(Number(this.customSize.height) || this.canvasSize.height || 1, 1);
      const scale = Math.min(70 / width, 80 / height);

      return {
        width: `${Math.max(Math.round(width * scale), 8)}px`,
        height: `${Math.max(Math.round(height * scale), 8)}px`,
      };
    },
    isCustomSizeValid() {
      const width = Number(this.customSize.width);
      const height = Number(this.customSize.height);

      return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;
    },
    customSizeButtonLabel() {
      if (!this.isCustomSizeValid) return 'Add W × H';

      return `Add ${Number(this.customSize.width)} × ${Number(this.customSize.height)}`;
    },
    submitButtonLabel() {
      if (!this.isMultiSizes) return 'Resize template';
      return `Create ${this.selectedSizes.length} ${this.selectedSizes.length === 1 ? 'size' : 'sizes'}`;
    },
    getBackgroundFields() {
      return this.fields.filter(field => [FIELD_TYPE_BACKGROND_THEME_IMAGE].includes(field.type));
    },
    selectedBackground: {
      get() {
        return this.fields.find(option => option.id === this.backgroundProperties.selectedField) || null;
      },
      set(option) {
        this.backgroundProperties.selectedField = option ? option.id : null;
      }
    },
  },
  watch: {
    getBackgroundFields(newOptions) {
      newOptions.map(item => {
        if(item.name === "Background"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Background Image"){
          this.backgroundProperties.selectedField = item.id;
        }
        if(item.name === "Image Area"){
          this.backgroundProperties.selectedField = item.id;
        }
      });
    }
  },
  mounted() {
    copyAndResizeEventBroker.on(EVENTS.INIT, this.init.bind(this));
    copyAndResizeEventBroker.on(EVENTS.CLOSED, this.onClose.bind(this));
    copyAndResizeEventBroker.on(EVENTS.PROGRESS, this.onProgress.bind(this));
  },
  methods: {
    init({ title, fields, isMultiSizes, canvasSize, objects, preview, preselectedSizes, instantRun }) {
      this.reset();
      this.title = title;
      this.isMultiSizes = isMultiSizes;
      this.fields = fields;
      this.canvasSize = canvasSize;
      this.customSize = {
        width: canvasSize.width,
        height: canvasSize.height,
      };
      this.maintainAspectRatio = true;
      this.objects = objects;
      this.isSaving = false;
      this.preview = preview;
      this.instantRun = instantRun;

      const customerId = preview?.data?.customer_id ?? preview?.template?.customer_id;
      this.selectedCustomer = this.customerOptions
        .find(customer => Number(customer.id) === Number(customerId)) || ALL_CUSTOMERS_OPTION;

      this.backgroundProperties.selectedField = this.getBackgroundFields
        .find(field => field.name === FIELD_NAME_IMAGE_AREA)?.id || null;

      if (preselectedSizes && preselectedSizes.length > 0) {
        const sizes = preselectedSizes.map(size => ({
          ...size,
          type: this.getTypeDimension(size.width, size.height),
        }));
        
        this.selectedSizes = sizes;
      }

      this.$bvModal.show(MODAL_ID);
    },
    getTypeDimension(width, height) {
      if (this.isProportionalDimension) {
        return TYPE_DIMENSION.PROPORTIONAL;
      }

      return getTypeDimension(width, height, this.canvasSize);
    },
    onRemoveSize(index) {
      this.selectedSizes = this.selectedSizes.filter((item, _index) => _index !== index);
    },
    clearSelectedSizes() {
      this.selectedSizes = [];
    },
    selectTab(tab) {
      this.activeTab = tab;
      const selectedCustomerId = this.selectedCustomer?.id;
      if (tab === TAB_TYPES.TEMPLATES && selectedCustomerId !== undefined && !this.templates[selectedCustomerId]) {
        this.fetchTemplates(selectedCustomerId);
      }
    },
    findSelectedSizeIndex({ width, height }) {
      return this.selectedSizes.findIndex(size =>
        Number(size.width) === Number(width) && Number(size.height) === Number(height)
      );
    },
    isSizeSelected(size) {
      return this.findSelectedSizeIndex(size) !== -1;
    },
    togglePresetSize(size) {
      const selectedIndex = this.findSelectedSizeIndex(size);
      if (selectedIndex !== -1) {
        this.onRemoveSize(selectedIndex);
        return;
      }

      this.addSize(size);
    },
    addSize({ width, height, template = null }) {
      const size = {
        width: Number(width),
        height: Number(height),
        type: this.getTypeDimension(Number(width), Number(height)),
        ...(template ? { template } : {}),
      };

      if (!this.isMultiSizes) {
        this.selectedSizes = [size];
        return;
      }

      const selectedIndex = this.findSelectedSizeIndex(size);
      if (selectedIndex === -1) {
        this.selectedSizes.push(size);
      } else {
        this.$set(this.selectedSizes, selectedIndex, size);
      }
    },
    getSizePreviewStyle({ width, height }) {
      const safeWidth = Math.max(Number(width) || 1, 1);
      const safeHeight = Math.max(Number(height) || 1, 1);
      const scale = Math.min(30 / safeWidth, 30 / safeHeight);

      return {
        width: `${Math.max(Math.round(safeWidth * scale), 4)}px`,
        height: `${Math.max(Math.round(safeHeight * scale), 4)}px`,
      };
    },
    templateIdentity(template) {
      return template?.id ?? template?.name;
    },
    findSelectedTemplateIndex(template) {
      const identity = this.templateIdentity(template);
      return this.selectedSizes.findIndex(size => this.templateIdentity(size.template) === identity);
    },
    getTemplateCustomerName(template) {
      return template.customer_name || this.customerOptions
        .find(customer => Number(customer.id) === Number(template.customer_id))?.name || '';
    },
    isTemplateSelected(template) {
      return this.findSelectedTemplateIndex(template) !== -1;
    },
    toggleTemplate(template) {
      const selectedIndex = this.findSelectedTemplateIndex(template);

      if (selectedIndex !== -1) {
        this.onRemoveSize(selectedIndex);
        return;
      }

      const size = {
        width: Number(template.width),
        height: Number(template.height),
        type: this.getTypeDimension(Number(template.width), Number(template.height)),
        template,
      };

      if (this.isMultiSizes) {
        this.selectedSizes.push(size);
      } else {
        this.selectedSizes = [size];
      }
    },
    onAddCustomSize(option = null) {
      const width = Number(option?.width ?? this.customSize.width);
      const height = Number(option?.height ?? this.customSize.height);

      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
        console.warn("Invalid size dimensions: width or height is missing.");
        return;
      }

      this.addSize({ width, height });
    },
    async onChangeCustomer(selectedCustomer) {
      if (!selectedCustomer) {
        this.selectedTemplates = [];
        return;
      }

      const selectedCustomerId = selectedCustomer.id;
      this.tags = [];
      this.onChangeTags()
      if (this.templates[selectedCustomerId]) return;

      this.selectedTemplates = [];
      await this.fetchTemplates(selectedCustomerId)
    },
    addGroup() {
      this.objects.push({
        name: this.inputGroup,
        isGroup: true,
        visible: false,
        objects: [],
      });
    },
    async fetchTemplates(id = null) {
      const selectedCustomerId = id ?? this.selectedCustomer?.id;
      if (selectedCustomerId === null || selectedCustomerId === undefined) return;
      this.isLoading = true;

      try {
        const { data } = await TEMPLATE_API.getTemplatesByCustomer(selectedCustomerId, {
          visible_customers_only: Number(selectedCustomerId) === ALL_CUSTOMERS_OPTION.id,
        });

        this.templates = {
          ...this.templates,
          [selectedCustomerId]: data
        };

        this.templatesFilteredByTags = {
          ...this.templatesFilteredByTags,
          [selectedCustomerId]: data
        };

      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    onClose() {
      this.$nextTick(() => {
        this.$bvModal.hide(MODAL_ID);
        this.reset();
      });
    },
    onSave() {
      this.isSaving = true;
      this.progress = {
        total: this.selectedSizes.length,
        current: 0
      };

      copyAndResizeEventBroker.fire(EVENTS.SAVED, {
        isUseDestinationTemplateFields: this.isUseDestinationTemplateFields,
        selectedSizes: this.selectedSizes,
        alignContent: this.alignContent,
        backgroundProperties: this.backgroundProperties,
        isMultiSizes: this.isMultiSizes,
        objects: this.objects,
        createBasetemplate: this.createBasetemplate,
        isAutoPosition: this.isAutoPosition,
      })
    },
    onProgress() {
      this.progress.current++;
    },
    onMaintainAspectRatioChange(value) {
      if (value) {
        this.customSize.width = this.canvasSize.width;
        this.customSize.height = this.canvasSize.height;
      }
    },
    onCustomSizeChange(value, inputType) {
      if (!this.maintainAspectRatio) return;
      
      const currentWidth = this.canvasSize.width;
      const currentHeight = this.canvasSize.height;

      const aspectRatio = currentWidth / currentHeight;

      let newWidth;
      let newHeight;

      if (inputType === CUSTOM_SIZE_INPUT_TYPES.WIDTH_INPUT) {
        newHeight = +value / aspectRatio;
        this.customSize.height = Math.round(newHeight);
        
      } else {
        newWidth = +value * aspectRatio;
        this.customSize.width = Math.round(newWidth);
        
      }
    },
    getTemplates() {
      const templates = this.templatesFilteredByTags[this.selectedCustomer?.id] || [];

      if (this.selectedTypeTemplate) {
        const handler = this.selectedTypeTemplate === 1 ?
            ({width, height}) =>  height < width :
            ({width, height}) =>  height > width;
        return templates.filter(handler);
      }

      return templates;
    },
    onChangeTags() {
      let filteredTemplates = [];

      if (!this.tags.length) {
        return this.templatesFilteredByTags = {
          ...this.templatesFilteredByTags,
          [this.selectedCustomer?.id]: this.templates[this.selectedCustomer?.id]
        }
      }

      this.tags.forEach(tag => {
        const templatesByTag = this.templates[this.selectedCustomer?.id].filter(item => item.tags === tag);

        filteredTemplates = filteredTemplates.concat(templatesByTag);
      });

      this.templatesFilteredByTags[this.selectedCustomer?.id] = filteredTemplates;
    },
    onShown() {
      if (this.instantRun && this.selectedSizes.length) {
        this.onSave();
      }
    },
    reset() {
      this.activeTab = TAB_TYPES.PRESETS;
      this.presetSearch = '';
      this.templateSearch = '';
      this.advancedOptionsVisible = false;
      this.selectedTypeTemplate = 0;
      this.galleryView = false;
      this.selectedSizes = [];
      this.selectedTemplates = [];
      this.selectedCustomer = null;
      this.tags = [];
      this.customSize = {
        width: '',
        height: '',
      };
      this.maintainAspectRatio = true;
      this.createBasetemplate = false;
      this.isAutoPosition = true;
      this.progress = {
        total: 0,
        current: 0,
      }
    }
  }
}
</script>

<style lang="scss">
#copy-and-resize-template-modal {
  background-color: rgba(0, 0, 21, .55);
  color: #172b4d;

  .resize-template-modal {
    max-width: 672px;
  }

  .modal-content {
    display: flex;
    max-height: calc(100vh - 30px);
    flex-direction: column;
    border: 0;
    border-radius: 6px;
    overflow: hidden;
  }

  .resize-template-modal__body {
    display: flex;
    min-height: 0;
    flex: 0 1 auto;
    flex-direction: column;
    padding: 0;
  }

  .modal-footer {
    display: block;
    flex: 0 0 auto;
    padding: 12px 20px;
    border-top: 1px solid #dfe5ee;
  }

  .resize-template-modal__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex: 0 0 auto;
    padding: 20px;
    border-bottom: 1px solid #dfe5ee;

    h5 {
      margin: 0 0 4px;
      color: #091e42;
      font-size: 16px;
      font-weight: 700;
    }

    .close {
      padding: 0;
      font-size: 20px;
    }
  }

  .resize-template-modal__original-size {
    color: #60789d;
    font-size: 12px;
  }

  .resize-template-modal__content {
    min-height: 0;
    flex: 0 1 auto;
    padding: 16px 20px 10px;
    overflow-y: auto;
  }

  .resize-template-modal__selected-sizes {
    margin-bottom: 14px;
  }

  .resize-template-modal__section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    color: #5e7292;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;

    button {
      padding: 0;
      border: 0;
      background: transparent;
      color: #50698f;
      font-size: 11px;
      font-weight: 400;
      text-transform: none;
    }
  }

  .resize-template-modal__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .resize-template-modal__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    border: 1px solid #afe0d3;
    border-radius: 4px;
    background: #e7f6f2;
    color: #159b79;
    font-size: 12px;
    font-weight: 700;
  }

  .resize-template-modal__tabs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 3px;
    border: 1px solid #d9e1ec;
    border-radius: 5px;

    button {
      padding: 7px 10px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #587094;
      font-size: 12px;
      font-weight: 600;
    }

    button.active {
      background: #159d7b;
      color: #fff;
    }

    svg {
      margin-right: 6px;
    }
  }

  .resize-template-modal__picker {
    height: 320px;
    margin-top: 14px;
    border: 1px solid #d9e1ec;
    border-radius: 6px;
    overflow: hidden;
  }

  .resize-template-modal__search {
    display: flex;
    align-items: center;
    height: 34px;
    border: 1px solid #d7dfeb;
    border-radius: 4px;
    background: #fff;

    svg {
      flex: 0 0 auto;
      margin-left: 10px;
      color: #657da1;
    }

    input {
      width: 100%;
      height: 100%;
      padding: 0 9px;
      border: 0;
      outline: 0;
      color: #172b4d;
      font-size: 13px;
    }
  }

  .resize-template-modal__picker > .resize-template-modal__search {
    margin: 8px;
  }

  .resize-template-modal__catalog {
    height: calc(100% - 51px);
    padding: 2px 12px 12px;
    border-top: 1px solid #e2e7ef;
    overflow-y: auto;
  }

  .resize-template-modal__preset-group {
    margin-top: 11px;
  }

  .resize-template-modal__group-title {
    margin-bottom: 6px;
    color: #526b91;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .resize-template-modal__card-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .resize-template-modal__size-card,
  .resize-template-modal__template-card {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: 50px;
    padding: 8px 10px;
    border: 1px solid #d8e0eb;
    border-radius: 4px;
    background: #fff;
    color: #172b4d;
    text-align: left;

    &.selected {
      border-color: #159d7b;
      background: #f1faf7;
    }

    > svg {
      margin-left: auto;
      color: #159d7b;
    }
  }

  .resize-template-modal__size-preview,
  .resize-template-modal__template-size-preview {
    display: flex;
    flex: 0 0 38px;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 32px;
    margin-right: 8px;
  }

  .resize-template-modal__template-size-preview > span {
    display: block;
    border: 1px solid #c8d3e2;
    background: #f4f7fa;
  }

  .resize-template-modal__size-preview {
    flex-basis: auto;
    margin-right: 10px;
    border: 1px solid #c8d3e2;
    background: #f4f7fa;
  }

  .resize-template-modal__size-card.selected .resize-template-modal__size-preview {
    border-color: #159d7b;
    background: #cdece4;
  }

  .resize-template-modal__template-card.selected .resize-template-modal__template-size-preview > span {
    border-color: #159d7b;
    background: #cdece4;
  }

  .resize-template-modal__card-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;

    strong,
    small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      color: #091e42;
      font-size: 12px;
    }

    small {
      color: #60789d;
      font-size: 10px;
    }
  }

  .resize-template-modal__template-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(150px, 1fr);
    gap: 8px;
    padding: 8px;
  }

  .resize-template-modal__customer {
    min-height: 34px;

    .multiselect__tags {
      min-height: 34px;
      padding-top: 6px;
      border-color: #d7dfeb;
      font-size: 12px;
    }

    .multiselect__select {
      height: 32px;
    }
  }

  .resize-template-modal__template-filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px 8px;
    border-bottom: 1px solid #e2e7ef;

    button {
      padding: 4px 8px;
      border: 0;
      border-radius: 3px;
      background: transparent;
      color: #587094;
      font-size: 10px;
    }

    button.active {
      background: #e3f3ee;
      color: #159d7b;
      font-weight: 700;
    }

    .custom-control-label {
      color: #60789d;
      font-size: 10px;
    }
  }

  .resize-template-modal__template-list {
    height: calc(100% - 83px);
    padding: 8px 12px;
    overflow-y: auto;

    &.gallery-view {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-content: start;
      gap: 8px;
    }
  }

  .resize-template-modal__template-card {
    width: 100%;
    margin-bottom: 8px;
  }

  .resize-template-modal__template-list.gallery-view .resize-template-modal__template-card {
    margin-bottom: 0;
  }

  .resize-template-modal__empty {
    padding: 40px 15px;
    color: #60789d;
    font-size: 12px;
    text-align: center;
  }

  .resize-template-modal__custom-size {
    display: grid;
    grid-template-columns: 220px 1fr;
    align-content: start;
    gap: 12px;
    height: 100%;
    padding: 16px;

    > .btn {
      grid-column: 1 / -1;
      border-color: #159d7b;
      background: #159d7b;

      &:disabled {
        border-color: #b8c4d3;
        background: #b8c4d3;
      }
    }
  }

  .resize-template-modal__custom-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 96px;
    background: #f6f8fa;

    span {
      display: block;
      border: 1px solid #bcc9da;
      background: #fff;
    }
  }

  .resize-template-modal__custom-controls {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .resize-template-modal__dimensions {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 8px;

    label {
      flex: 1;
      margin: 0;
      color: #526b91;
      font-size: 9px;
    }

    span {
      padding-bottom: 8px;
      font-size: 12px;
    }

    input {
      margin-top: 4px;
    }
  }

  .resize-template-modal__advanced-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px 4px;
    border: 0;
    background: transparent;
    color: #50698f;
    font-size: 12px;
    text-align: left;

    span svg {
      margin-right: 7px;
    }
  }

  .resize-template-modal__advanced {
    padding-bottom: 2px;

    > label {
      margin-bottom: 7px;
      color: #091e42;
      font-size: 12px;
      font-weight: 700;
    }
  }

  .resize-template-modal__option-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 42px;
    margin-top: 10px;
    padding: 8px 12px;
    border: 1px solid #d9e1ec;
    border-radius: 4px;
    color: #172b4d;
    font-size: 11px;
  }

  .resize-template-modal__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    .btn-link {
      color: #091e42;
      text-decoration: none;
    }

    .btn-primary {
      border-color: #159d7b;
      background: #159d7b;
    }
  }

  .background-dropdown-label {
    display: flex;
    align-items: center;
    column-gap: 10px;
    
    &__image {
      position: relative;
      padding-top: 50px;
      width: 100%;
      max-width: 50px;
      
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
    
    &__text {
      margin-bottom: 0;
    }
  }
  .background-dropdown-option {
    display: flex;
    align-items: center;
    column-gap: 10px;
    
    &__image {
      position: relative;
      width: 100%;
      max-width: 30px;
      padding-top: 30px;
      
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
    
    &__text {
      margin: 0;
    }
  }
  
  .background-dropdown {
    .multiselect__select {
      top: 50%;
      transform: translateY(-50%);
    }
  }

  @media (max-width: 767px) {
    .resize-template-modal__card-grid,
    .resize-template-modal__custom-size {
      grid-template-columns: 1fr;
    }

    .resize-template-modal__custom-size > .btn {
      grid-column: 1;
    }
  }
}
</style>
